import { useQuery } from '@tanstack/react-query'
import activityReportService from '@/services/activityReportService'
import { useApiReady } from '@/hooks/useApiReady'

// Query keys
export const activityReportsKeys = {
  all: ['activityReports'] as const,
  lists: () => [...activityReportsKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...activityReportsKeys.lists(), filters] as const,
}

export interface ActivityReportsFilters {
  page?: number
  pageSize?: number
  search?: string
  status?: string[]
  submissionDate?: string
  missionId?: string
  /** Mois du CRA (YYYY-MM), aligné avec le filtre période comptabilité */
  reportMonth?: string
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
  teamId?: string
  freelancerId?: string
  umbrella?: 'yes' | 'no'
  isFavorite?: boolean
}

export function resolveActivityReportsStatusForApi(
  isFreelancer: boolean,
  status: string[] | undefined
): string[] | undefined {
  if (isFreelancer) {
    return Array.isArray(status) ? status : undefined
  }
  if (Array.isArray(status) && status.length === 0) {
    return ['pending', 'approved']
  }
  if (!Array.isArray(status)) {
    return undefined
  }
  const withoutDraft = status.filter((s) => s !== 'draft')
  if (withoutDraft.length === 0) {
    return ['pending', 'approved']
  }
  return withoutDraft
}

function activityReportsListQueryKey(
  filters: ActivityReportsFilters,
  statusForApi: string[] | undefined
) {
  return activityReportsKeys.list({
    page: filters.page,
    pageSize: filters.pageSize,
    statusFilter: statusForApi,
    submissionDateFilter: filters.submissionDate,
    missionFilter: filters.missionId,
    reportMonth: filters.reportMonth,
    sortConfig: { key: filters.sortBy, direction: filters.sortDirection },
    teamId: filters.teamId,
    searchTerm: filters.search,
    freelancerId: filters.freelancerId,
    umbrella: filters.umbrella,
    isFavorite: filters.isFavorite,
  })
}

function buildActivityReportsQueryFn(
  filters: ActivityReportsFilters,
  statusForApi: string[] | undefined,
  isFreelancer: boolean,
  effectiveTeamId: string | null | undefined
) {
  return async () => {
    if (isFreelancer && !effectiveTeamId) {
      return { list: [], total: 0, accountingToolEmail: '' }
    }

    const { success, data: resp } = await activityReportService.getReports({
      page: filters.page,
      pageSize: filters.pageSize,
      search: filters.search,
      status: Array.isArray(statusForApi) && statusForApi.length > 0 ? statusForApi : undefined,
      submissionDate: filters.submissionDate,
      missionId: filters.missionId !== 'all' ? filters.missionId : undefined,
      reportMonth: filters.reportMonth,
      sortBy: filters.sortBy,
      sortDirection: filters.sortDirection,
      teamId: isFreelancer ? effectiveTeamId : filters.teamId,
      freelancerId: filters.freelancerId,
      umbrella: filters.umbrella,
      isFavorite: filters.isFavorite,
    })

    if (success) {
      let filteredData = resp
      if (resp?.list) {
        let filteredList = resp.list

        if (!isFreelancer) {
          filteredList = filteredList.filter((item) => item.status !== 'draft')
        }

        if (!isFreelancer) {
          const hasRejectedInFilter =
            Array.isArray(statusForApi) && statusForApi.includes('rejected')
          if (!hasRejectedInFilter) {
            filteredList = filteredList.filter((item) => item.status !== 'rejected')
          }
        }

        filteredData = {
          ...resp,
          list: filteredList,
          total: resp.total,
        }
      }
      return filteredData
    }

    return { list: [], total: 0, accountingToolEmail: '' }
  }
}

export function getActivityReportsQueryOptions(
  filters: ActivityReportsFilters,
  options?: { isFreelancer?: boolean; effectiveTeamId?: string | null }
) {
  const isFreelancer = options?.isFreelancer ?? false
  const effectiveTeamId = options?.effectiveTeamId
  const statusForApi = resolveActivityReportsStatusForApi(isFreelancer, filters.status)

  return {
    queryKey: activityReportsListQueryKey(filters, statusForApi),
    queryFn: buildActivityReportsQueryFn(filters, statusForApi, isFreelancer, effectiveTeamId),
  }
}

/**
 * Hook pour récupérer la liste des activity reports
 */
export function useActivityReports(
  filters: ActivityReportsFilters,
  options?: { enabled?: boolean; isFreelancer?: boolean; effectiveTeamId?: string | null }
) {
  const isFreelancer = options?.isFreelancer || false
  const effectiveTeamId = options?.effectiveTeamId
  const apiReady = useApiReady()

  return useQuery({
    ...getActivityReportsQueryOptions(filters, { isFreelancer, effectiveTeamId }),
    enabled:
      apiReady &&
      options?.enabled !== false &&
      (!isFreelancer || !!effectiveTeamId),
  })
}
